import * as React from 'react';

import {
  Button,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogOverlay,
} from '@rematter/ui-web';
import { AlertTriangle } from 'lucide-react';

import { useTranslations } from '../../i18n/useTranslations';

interface DeleteAutomationDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  automationName: string;
  onConfirm: () => void | Promise<void>;
  isDeleting?: boolean;
}

export const DeleteAutomationDialog: React.FC<DeleteAutomationDialogProps> = ({
  isOpen,
  onOpenChange,
  automationName,
  onConfirm,
  isDeleting = false,
}) => {
  const t = useTranslations();

  const handleClose = () => {
    if (!isDeleting) {
      onOpenChange(false);
    }
  };

  const handleConfirm = async () => {
    await onConfirm();
  };

  return (
    <DialogOverlay
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      className="@DeleteAutomationDialog"
    >
      <DialogContent position="center" className="w-full min-w-[32rem]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl font-semibold text-failure">
            <AlertTriangle className="size-5" />
            {t.dialogs.delete.title}
          </DialogTitle>
        </DialogHeader>

        <div className="p-4">
          <p className="text-sm text-neutral/70">
            {t.dialogs.delete.message(automationName)}
          </p>
          <p className="mt-2 text-sm text-failure/80">
            {t.dialogs.delete.warning}
          </p>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onPress={handleClose}
            isDisabled={isDeleting}
          >
            {t.dialogs.delete.cancel}
          </Button>
          <Button
            variant="solid"
            onPress={handleConfirm}
            isDisabled={isDeleting}
            isLoading={isDeleting}
            className="bg-failure text-white hover:bg-failure/90"
          >
            {t.dialogs.delete.confirm}
          </Button>
        </DialogFooter>
      </DialogContent>
    </DialogOverlay>
  );
};
